/**
 * 🧱 Refactor BT Phase 3 — Étape 2 : section « SectionZones » d'EditerBonTravailDialog.jsx.
 * JSX déplacé TEL QUEL (ex-L2717-2833, dédenté de 10) ; variables lues via `useBTForm()`
 * (BTFormProvider enrichi par `extra` dans le composant parent). DOM, data-testid, classes, textes inchangés.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Upload, X } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useBTForm } from '../../BTFormContext';
import { urlFichier } from '@/lib/fichiers';

export function SectionZones() {
  const {
    addZone,
    canDoBonTravailAction,
    form,
    handleZonePhotoUpload,
    isExt,
    removeZone,
    setZonePhotoFullscreen,
    type,
    updateZone,
    uploadingZone,
  } = useBTForm();
  return (
    <>
      {/* Zones (extérieur seulement) */}
      {isExt && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-base">Zones</CardTitle>
              {(!canDoBonTravailAction || canDoBonTravailAction('bon_ajouter_zones')) && (
                <Button type="button" size="sm" variant="outline" onClick={addZone}><Plus className="w-3 h-3 mr-1" /> Ajouter zone</Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {form.zones.length === 0 ? (
              <p className="text-sm text-gray-500">Aucune zone définie</p>
            ) : (
              // 🆕 2026-05-08 — Layout sobre : 1 zone par ligne, séparation visuelle claire
              // sans arc-en-ciel. Bandeau gris foncé en tête + bordure gauche bleue discrète.
              <div className="space-y-3">
                {form.zones.map((z, idx) => (
                  <div
                    key={z.id || z._id || `zone-${idx}`}
                    className="border-2 border-slate-200 border-l-4 border-l-blue-500 rounded-lg overflow-hidden bg-white shadow-sm"
                    data-testid={`zone-card-${idx}`}
                  >
                    {/* Bandeau de tête neutre — gros numéro + nom + delete */}
                    <div className="bg-slate-100 px-3 py-2 flex items-center justify-between gap-2 border-b border-slate-200">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="bg-slate-700 text-white rounded-md w-9 h-9 flex items-center justify-center text-sm font-bold flex-shrink-0">
                          Z{z.numero}
                        </span>
                        <span className="font-semibold text-sm text-slate-800 truncate" data-testid={`zone-titre-${idx}`}>
                          {z.nom?.trim() || `Zone ${z.numero}`}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeZone(idx)}
                        className="text-red-500 hover:bg-red-50 hover:text-red-700 flex-shrink-0"
                        title="Supprimer cette zone"
                        data-testid={`zone-delete-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Corps : nom + détails à gauche, photo à droite (md+) */}
                    <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <div>
                          <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Nom de la zone</Label>
                          <Input
                            value={z.nom}
                            onChange={(e) => updateZone(idx, 'nom', e.target.value)}
                            placeholder="Ex : Salon, Façade nord, Plafond…"
                            className="mt-0.5"
                            data-testid={`zone-nom-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Détails / Travaux à faire</Label>
                          <Textarea
                            value={z.description || ''}
                            onChange={(e) => updateZone(idx, 'description', e.target.value)}
                            placeholder="Peinture, préparation, traitements, nombre de couches, couleur…"
                            rows={5}
                            className="mt-0.5 text-sm"
                            data-testid={`zone-description-${idx}`}
                          />
                        </div>
                      </div>
                      <div>
                        {z.photo_url ? (
                          <div className="relative">
                            <img
                              src={urlFichier(z.photo_url)}
                              alt={`Zone ${z.numero}`}
                              className="w-full h-48 object-contain rounded bg-slate-50 border cursor-zoom-in"
                              onClick={() => setZonePhotoFullscreen({ url: z.photo_url, nom: z.nom, numero: z.numero, description: z.description })}
                              data-testid={`zone-photo-${idx}`}
                              title="Cliquer pour agrandir"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="absolute top-1 right-1"
                              onClick={(e) => { e.stopPropagation(); updateZone(idx, 'photo_url', null); }}
                              title="Retirer la photo"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg h-48 cursor-pointer hover:bg-gray-50 bg-slate-50/50">
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleZonePhotoUpload(idx, e.target.files[0])} />
                            {uploadingZone === idx ? (
                              <span className="text-sm text-gray-500">Upload…</span>
                            ) : (
                              <>
                                <Upload className="w-7 h-7 text-gray-400 mb-1" />
                                <span className="text-sm text-gray-500">Ajouter une photo</span>
                              </>
                            )}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

    </>
  );
}
